import React from 'react';
import { Box, Paragraph, Text } from 'bumbag';

import LocationPermissionsInstructions from './LocationPermissionsInstructions';

import { ERROR_MESSAGES, POSITION_ERROR_CODES } from '../../../constants';

interface LocationPermissionsRejectedProps {
  error: Record<string, any> | null | undefined;
}

const GeneralisedErrorMessage = () => (
  <Paragraph>
    To try again, click <Text fontWeight="semibold">Go back & try again</Text> below, or ask your provider to re-send
    the payment request.
  </Paragraph>
);

const PatientRejectedErrorMessage = () => (
  <Paragraph>
    If you did this in error, click <Text fontWeight="semibold">GO BACK & TRY AGAIN</Text> below.
  </Paragraph>
);

const PermissionDeniedErrorMessage = () => (
  <Box marginBottom="major-2">
    Please enable location services via your browser or device settings using the instructions below:
    <LocationPermissionsInstructions />
  </Box>
);

const PositionUnavailableErrorMessage = () => (
  <Box marginBottom="major-2">
    Please make sure location services can identify your current location using the instructions below:
    <LocationPermissionsInstructions />
  </Box>
);

const TimeoutErrorMessage = () => (
  <Box marginBottom="major-2">
    Please refresh the page, then try again with a stable internet connection using the instructions below:
    <LocationPermissionsInstructions />
  </Box>
);

const LocationPermissionsRejected = ({ error }: LocationPermissionsRejectedProps) => {
  const didPatientReject = !error;
  let errorDescription = ERROR_MESSAGES.LOCATION_PERMISSIONS_ERRORED;

  if (error && error.code === POSITION_ERROR_CODES.PERMISSION_DENIED) {
    errorDescription = ERROR_MESSAGES.LOCATION_PERMISSIONS_DENIED;
  }
  if (error && error.code === POSITION_ERROR_CODES.POSITION_UNAVAILABLE) {
    errorDescription = ERROR_MESSAGES.LOCATION_PERMISSIONS_POSITION_UNAVAILABLE;
  }
  if (error && error.code === POSITION_ERROR_CODES.TIMEOUT) {
    errorDescription = ERROR_MESSAGES.LOCATION_PERMISSIONS_TIMEOUT;
  }

  let errorHelpRenderer = GeneralisedErrorMessage;

  if (didPatientReject) {
    errorHelpRenderer = PatientRejectedErrorMessage;
  }

  if (error && error.code === POSITION_ERROR_CODES.PERMISSION_DENIED) {
    errorHelpRenderer = PermissionDeniedErrorMessage;
  }
  if (error && error.code === POSITION_ERROR_CODES.POSITION_UNAVAILABLE) {
    errorHelpRenderer = PositionUnavailableErrorMessage;
  }
  if (error && error.code === POSITION_ERROR_CODES.TIMEOUT) {
    errorHelpRenderer = TimeoutErrorMessage;
  }

  return (
    <Box>
      {error && <Paragraph>{errorDescription}</Paragraph>}
      <Paragraph>Without your location, we are unable to process a private health insurance claim.</Paragraph>
      {errorHelpRenderer()}
      <Paragraph>
        Alternatively, you can choose to not share your location but you will be required to pay for the service fully
        out of pocket, and submit a claim with your private health insurer separately.
      </Paragraph>
    </Box>
  );
};

export default LocationPermissionsRejected;
